import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { IState, ReduxMap } from '../../redux/redux';
import { ServerResponse } from '../../utils/Server';

/**
 * NHD-only bulk entry import (replaces Admin/BulkLoader.cshtml).
 * Fixed 13-column CSV template via Admin/EntryUpload.
 */
const BulkLoaderPage = (props: IState) => {
  const isNhd = props.Settings?.isHistoryContest === true;
  const [status, setStatus] = useState('');
  const [processing, setProcessing] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [sendLoginInfo, setSendLoginInfo] = useState(false);
  const [register, setRegister] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const templateHref = useMemo(() => StoreX.BuildUrl('/Files/ImportTemplate.csv'), []);

  if (props.Settings && !isNhd) {
    return (
      <div className="main-body-page grow bumper-l" data-testid="bulk-loader-page">
        <div className="alert alert-warning">
          <h3>NHD Only</h3>
          This bulk loader is available for National History Day events only. Use the standard bulk importer for other
          contests.
        </div>
        <a className="btn btn-default" href={StoreX.BuildUrlWithFair('/app/import')}>
          Open Bulk Importer
        </a>
      </div>
    );
  }

  const processFile = () => {
    if (!file) {
      toast.warning('Please select a CSV file first.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const lines = text.split('\n');
      let columns = -1;
      let message = '';
      let pass = true;

      for (let i = 0; i < lines.length; i++) {
        const parts = lines[i].split(',');
        if (columns === -1) {
          columns = parts.length;
        } else if (columns !== parts.length && parts.length > 1) {
          message += 'Error: The number of columns is not consistent.\n';
          pass = false;
          break;
        }
      }

      if (columns !== 13) {
        pass = false;
        message += 'Error: It appears you are not using our template.\n';
      }

      if (lines.length <= 1) {
        pass = false;
        message += 'It appears that the file does not contain any entries to be imported.\n';
      }

      if (pass) {
        message += `Starting the import of ${lines.length - 1} students.\n`;
        message += 'Students are being sent to the server for processing; this may take a few minutes.\n';
      }

      setStatus(`Lines: ${lines.length} Columns: ${columns}\n\n${message}`);

      if (!pass) {
        toast.error('CSV validation failed. See status for details.', { autoClose: false });
        return;
      }

      setProcessing(true);
      store.server
        .postApi<ServerResponse<string>>('../Admin/EntryUpload', {
          fileText: text,
          sendWelcomeEmail,
          register,
          sendLoginInfo,
        })
        .then((data) => {
          if (data.Success) {
            setStatus((prev) => `${prev}DONE!\n`);
            toast.success('Import finished.');
          } else {
            setStatus((prev) => `${prev}Error:\n${data.Message || 'Unknown error'}\n`);
            toast.error(data.Message || 'Import failed.', { autoClose: false });
          }
        })
        .catch(() => {
          setStatus((prev) => `${prev}Server Error!\n`);
          toast.error('Server error while importing.', { autoClose: false });
        })
        .finally(() => setProcessing(false));
    };
    reader.readAsText(file);
  };

  return (
    <div className="main-body-page grow bumper-l" data-testid="bulk-loader-page">
      <h2>Bulk Loader</h2>
      <p>
        If you need to import entries from a contest that did not use our software, that can be done on this page.
        Please note that this is not a general feature and support is limited.{' '}
        <strong>It is very important that you follow the instructions.</strong> Use this CSV file as your{' '}
        <a href={templateHref}>Template File</a>. Open it with Excel, add the students you want to import, and do not
        change the format. Only CSV files are supported.
      </p>

      <h4>Import Entries Steps</h4>
      <ol>
        <li>
          Download CSV <a href={templateHref}>Template File</a>
        </li>
        <li>Open the file with Excel</li>
        <li>Add entries to the file</li>
        <li>Remove all commas from free-text fields in Excel</li>
        <li>Save as CSV and keep formatting</li>
        <li>Select your CSV file below</li>
        <li>Press Process</li>
        <li>Wait for the status message when processing finishes</li>
      </ol>

      <div className="alert alert-warning">
        <ul className="mb-0">
          <li>If a matching category cannot be found on upload, one will be assigned. Category names must match exactly.</li>
          <li>If a teacher is not provided, &quot;Teacher Unknown&quot; will be assigned.</li>
        </ul>
      </div>

      <div className="panel panel-primary" style={{ marginTop: '1.5rem' }}>
        <div className="panel-heading">Import Entries Form</div>
        <div className="panel-body">
          <div className="form-horizontal">
            <div className="form-group">
              <label className="control-label col-sm-3" htmlFor="csvFile">
                CSV File:
              </label>
              <div className="col-sm-6">
                <input
                  id="csvFile"
                  type="file"
                  accept=".csv,text/csv"
                  className="form-control"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-9 col-sm-offset-3">
                <label>
                  <input
                    type="checkbox"
                    checked={sendWelcomeEmail}
                    onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  />{' '}
                  Send Welcome Email
                </label>
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-9 col-sm-offset-3">
                <label>
                  <input type="checkbox" checked={sendLoginInfo} onChange={(e) => setSendLoginInfo(e.target.checked)} />{' '}
                  Send username &amp; password (must be registered)
                </label>
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-9 col-sm-offset-3">
                <label>
                  <input type="checkbox" checked={register} onChange={(e) => setRegister(e.target.checked)} /> Register
                  uploaded participants
                </label>
                <div className="alert alert-info" style={{ marginTop: '0.75rem' }}>
                  If you do not register uploaded participants, they will be listed as partially registered. When a
                  participant is partially registered and later registers with the same name and email, accounts will
                  merge.
                </div>
              </div>
            </div>
            <div className="form-group">
              <div className="col-sm-5 col-sm-offset-3">
                <button type="button" className="btn btn-primary" disabled={processing} onClick={processFile}>
                  <i className="fa fa-upload" /> {processing ? 'Processing…' : 'Process'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="control-label" htmlFor="fileStatus">
                Process Status:
              </label>
              <div className="col-sm-12">
                <textarea id="fileStatus" rows={8} className="form-control" readOnly value={status} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReduxMap(BulkLoaderPage);
